import React from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import { useBaseModal } from 'utils';
import { GoalSelector } from '../../../progressReport/goalSelector';
import { fillMentions } from '../../../hooks/useAutoFillMentions';
import {
    expandGoalRows,
    expandTargetRows,
    GOAL_MODE_GOALS_KEY,
    repeatFirstRowLayout,
    targetModeRowSpecs,
} from '../../../hooks/goalModeTable';

/**
 * Goal Mode on the note fill surfaces (clinical notes).
 *
 * A Goal Mode field is filled per goal: staff pick goals ("Add Goal") from all
 * of the client's goals, which the api returns with the note's default values
 * (`formDefault[GOAL_MODE_GOALS_KEY]`), and the field repeats once per picked
 * goal with that goal's tokens filled in:
 *
 *  - RICH_TEXT — one editor with the template filled once per goal; it is the
 *    field's `<prefix>.value`, so every read-only renderer (previews, members
 *    portal, PDF) shows it unchanged.
 *  - TABLE — one row per goal (`hooks/goalModeTable.js`), each tagged `_goalId`.
 *    With Target Mode (`properties.targetMode`) on, each goal in the picker
 *    opens a dropdown of its targets, and there is one row per picked target
 *    instead, filled with its goal's tokens and its own and tagged `_goalId` +
 *    `_targetId`. A picked goal with no targets gets one row of its own (goal
 *    tokens filled, target tokens blank, no `_targetId`).
 *
 * The field's own value before the first pick is its template (goal tokens still
 * literal); it is kept at `<prefix>.goalModeTemplate` so goals can be added or
 * removed later.
 */

export const useGoalModeGoals = () => useSelector((state) => state.forms.formDefault?.[GOAL_MODE_GOALS_KEY]) || [];
const useGoalTokens = () => {
    const formDefault = useSelector((state) => state.forms.formDefault);
    const goals = useGoalModeGoals();
    return (goalId) => ({ ...formDefault, ...goals.find((goal) => goal.id === goalId) });
};

/**
 * Client of the note being filled. `GOAL_MODE_GOALS` entries carry no client id,
 * so it comes from the route on the client page / progress report, and from the
 * open appointment on the schedule. The route wins: off the schedule,
 * `appointmentById` can still hold an appointment opened earlier.
 */
const useNoteClientId = () => {
    const { pathname } = useLocation();
    const appt = useSelector((state) => state.appointment.appointmentById);
    return (
        matchPath('/client/:id', pathname)?.params.id ||
        matchPath('/progress-report/:id/client/:clientId/*', pathname)?.params.clientId ||
        appt?.client?.id ||
        appt?.client?._id
    );
};

const valuePrefix = (name) => name.replace(/\.value$/, '');

/**
 * "+ Add Goal" opens the same GoalSelector the progress report uses, in the
 * base modal: the note being filled is in the primary modal (over appt details in
 * the secondary one, from the schedule), and replacing either would lose it.
 *
 * With `withTargets`, every goal row opens a dropdown of its targets, and
 * `onDone` also gets the picked target ids. `goalTypes` lists only goals of those types.
 */
export const GoalModePicker = ({ selectedIds = [], onDone, withTargets, selectedTargetIds, goalTypes }) => {
    const goals = useGoalModeGoals();
    const { openBase, closeBase } = useBaseModal();
    const clientId = useNoteClientId();

    const handleSave = (list, targetIds) => {
        const picked = list.map((goal) => goal.id);
        // In the client's goal order, so the blocks / rows read the same way every time.
        // Only goals the note has tokens for can be filled.
        onDone(
            goals.filter((goal) => !goalTypes?.length || goalTypes.includes(goal.type)).map((goal) => goal.id).filter((id) => picked.includes(id)),
            targetIds,
        );
    };

    const openSelector = () =>
        openBase(
            clientId ? (
                <GoalSelector
                    clientId={clientId}
                    selectedGoals={selectedIds.map((id) => ({ id }))}
                    saveSelectedIds={handleSave}
                    onClose={closeBase}
                    skipClinicalData
                    goalTargets={withTargets ? Object.fromEntries(goals.map((goal) => [goal.id, goal.targets || []])) : undefined}
                    selectedTargetIds={selectedTargetIds}
                    goalTypes={goalTypes}
                />
            ) : (
                <p className="goal-mode-picker-empty">This client has no goals.</p>
            ),
        );

    return (
        <button type="button" className="goal-section-select-btn" onClick={openSelector}>
            + Add Goal
        </button>
    );
};

/**
 * A Goal Mode RICH_TEXT field: one editor holding the template filled once per
 * picked goal, in the client's goal order. Before any goal is picked it shows a
 * prompt instead of the editor — the template alone has nothing filled.
 *
 * The picked ids are kept at `<prefix>.goalModeGoalIds`. Adding goals appends
 * their filled text, so what staff already typed stays; removing a goal
 * rebuilds the text from the template for the goals left, because which part of
 * an edited text belongs to which goal cannot be told apart.
 *
 * `renderEditor(value, onChange)` draws the editor the way the surface draws any
 * rich text, so both fill surfaces keep their own editor props.
 */
export const GoalModeRichText = ({ item, name, watch, getValues, setValue, renderEditor }) => {
    const tokensFor = useGoalTokens();
    const prefix = valuePrefix(name);
    const goalIds = watch(`${prefix}.goalModeGoalIds`) || [];

    const fillFor = (template, ids) => ids.map((goalId) => fillMentions(template, tokensFor(goalId))).join('');

    const handleDone = (nextIds) => {
        const template = getValues(`${prefix}.goalModeTemplate`) ?? getValues(name) ?? item?.value ?? '';
        const currentIds = getValues(`${prefix}.goalModeGoalIds`) || [];
        const onlyAdded = currentIds.length && currentIds.every((id) => nextIds.includes(id));
        const value = !nextIds.length
            ? template
            : onlyAdded
              ? `${getValues(name) || ''}${fillFor(template, nextIds.filter((id) => !currentIds.includes(id)))}`
              : fillFor(template, nextIds);

        setValue(`${prefix}.goalModeTemplate`, template);
        setValue(`${prefix}.goalModeGoalIds`, nextIds, { shouldDirty: true });
        setValue(name, value, { shouldDirty: true });
    };

    return (
        <>
            <GoalModePicker selectedIds={goalIds} onDone={handleDone} />
            {goalIds.length ? (
                renderEditor(watch(name) ?? '', (value) => setValue(name, value))
            ) : (
                <p className="goal-mode-picker-empty">Select goals with + Add Goal to see their data here.</p>
            )}
        </>
    );
};

/**
 * A Goal Mode TABLE: "Add Goal" plus the table (`children`, drawn by the surface)
 * with one row per picked goal from the template row — or, with `targetMode`,
 * one row per picked target (picked from the goal's dropdown in the picker).
 * Before anything is picked it shows a prompt instead of the table — the
 * template row has nothing filled.
 */
export const GoalModeTable = ({ name, watch, getValues, setValue, targetMode, children }) => {
    const goals = useGoalModeGoals();
    const tokensFor = useGoalTokens();
    const prefix = valuePrefix(name);
    const rows = watch(name) || [];
    const goalIds = [...new Set(rows.map((row) => row?._goalId).filter(Boolean))];

    // `rowFor(current, template)` per picked goal / target: its existing row, so
    // what staff typed stays, or a new one from the template.
    const applyRows = (rowBuilders) => {
        const current = getValues(name) || [];
        const template = getValues(`${prefix}.goalModeTemplate`) ?? current[0];
        if (!template) return;
        const next = rowBuilders.length ? rowBuilders.map((rowFor) => rowFor(current, template)) : [template];
        setValue(`${prefix}.goalModeTemplate`, template);
        setValue(name, next, { shouldDirty: true });
        const table = getValues(`${prefix}.properties.table`);
        if (table) setValue(`${prefix}.properties.table`, repeatFirstRowLayout(table, next.length), { shouldDirty: true });
    };

    const handleGoals = (nextIds) =>
        applyRows(
            nextIds.map(
                (goalId) => (current, template) =>
                    current.find((row) => row._goalId === goalId && !row._targetId) ||
                    expandGoalRows(template, [{ ...tokensFor(goalId), id: goalId }])[0]
            )
        );

    // Picked targets of the picked goals, in goal order, then the goal's target order;
    // a picked goal with no targets gets one goal row with its target tokens blank.
    const handleTargets = (goalIdsPicked, targetIds = []) =>
        applyRows(
            targetModeRowSpecs(goals, goalIdsPicked, targetIds).map(
                ({ goal, target }) =>
                    (current, template) =>
                        current.find((row) => row._goalId === goal.id && (target ? row._targetId === target.id : !row._targetId)) ||
                        expandTargetRows(template, [{ ...tokensFor(goal.id), ...target, goalId: goal.id, targetId: target?.id }])[0]
            )
        );

    return (
        <>
            <GoalModePicker
                selectedIds={goalIds}
                onDone={targetMode ? handleTargets : handleGoals}
                withTargets={targetMode}
                selectedTargetIds={rows.map((row) => row?._targetId).filter(Boolean)}
            />
            {goalIds.length ? (
                children
            ) : (
                <p className="goal-mode-picker-empty">
                    {targetMode
                        ? 'Select goals and targets with + Add Goal to see their data here.'
                        : 'Select goals with + Add Goal to see their data here.'}
                </p>
            )}
        </>
    );
};
